import { message } from "antd";
import dayjs from "dayjs";

import { createSlice } from "@reduxjs/toolkit";

import { AppThunk } from "../../app/store";
import { getUserRequestsStatementBetweenTwoDates } from "../../services/userRequestApi";
import {
  AdminTypeId,
  endDateValue,
  startDateValue,
} from "../../shared/constants/Resources";
import UserRequestByDateDTO from "../../shared/dtos/userRequestExportDTO";
import {
  selectCurrentAdminId,
  selectCurrentAdminType,
} from "../../utils/helpers/selectorHelper";

interface Filters {
  startDate: string;
  endDate: string;
  page: number;
  take: number;
  searchText: string | undefined;
  bookingId?: string | null;
  shouldSendCompany?: boolean;
  status: string | undefined;
}

const defaultFilters: Filters = {
  startDate: startDateValue,
  endDate: endDateValue,
  page: 1,
  take: 10,
  searchText: undefined,
  bookingId: undefined,
  shouldSendCompany: true,
  status: undefined,
};

const initialState = {
  value: {
    userRequestByDateDto: new Array<UserRequestByDateDTO>(),
    total: 0,
  },
  loading: false,
  filtersUserRequestByDateDTO: structuredClone(defaultFilters),
};

export const userRequestByDateListSlice = createSlice({
  name: "userRequestListExport",
  initialState,
  reducers: {
    updateUserRequestByDateValue: (state, actions) => {
      state.value = actions.payload;
    },
    setLoadingUserRequestByDateValue: (state, actions) => {
      state.loading = actions.payload;
    },
    setFiltersUserRequestByDateDTO: (state, actions) => {
      state.filtersUserRequestByDateDTO = actions.payload
        ? actions.payload
        : structuredClone(defaultFilters);
    },
  },
});

export const {
  updateUserRequestByDateValue,
  setLoadingUserRequestByDateValue,
  setFiltersUserRequestByDateDTO,
} = userRequestByDateListSlice.actions;

export const userRequestByDateListAsync = ({
  startDate,
  endDate,
  page,
  take = 10,
  bookingId,
  shouldSendCompany,
  searchText,
  status,
}: Filters): AppThunk => {
  return async (dispatch) => {
    const adminId =
      selectCurrentAdminType() === AdminTypeId ? selectCurrentAdminId() : null;
    try {
      dispatch(setLoadingUserRequestByDateValue(true));
      const response: any = await getUserRequestsStatementBetweenTwoDates({
        startDate: dayjs(startDate).toDate(),
        endDate: dayjs(endDate).toDate(),
        page,
        take,
        bookingId,
        searchText,
        shouldSendCompany,
        adminId,
        status,
      });
      const trips = response.data;
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      for (const trip of trips) {
        if (!trip.startedAt || !trip.isUtc) continue;

        const startedAt = trip.startedAt;
        trip.startedAt = trip.isUtc
          ? dayjs.utc(startedAt).tz(userTimezone).format("DD-MM-YYYY HH:mm")
          : dayjs.utc(startedAt).format("DD-MM-YYYY HH:mm");
      }

      const userRequestByDateListObject = {
        userRequestByDateDto: trips,
        total: response.total,
        totalSpent: response.totalSpent,
      };
      dispatch(updateUserRequestByDateValue(userRequestByDateListObject));
    } catch (error) {
      message.error("Ha ocurrido un error al intentar obtener los datos.");
    } finally {
      dispatch(setLoadingUserRequestByDateValue(false));
    }
  };
};

export default userRequestByDateListSlice.reducer;
